import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccountService } from '../../../core/account.service';
import { VConfigService } from '../../../vconfig/vconfig.service';
import {
  ActivatableItemCount,
  ActivatablePackage,
  SelectedItem,
  SelectedItems,
  SortDirection,
} from '../../core/interface';
import { PackageActivationTableService } from './package-activation-table.service';

@Component({
  selector: 'app-paginated-package-activation-table',
  templateUrl: './paginated-package-activation-table.component.html',
  styleUrls: ['./paginated-package-activation-table.component.scss'],
  providers: [PackageActivationTableService],
  standalone: false,
})
export class PaginatedPackageActivationTableComponent implements OnInit {
  totalPackages$: Observable<number>;
  selectedItems$: Observable<SelectedItems>;
  activatablePackages$: Observable<ActivatablePackage[]>;
  pageOptions = [5, 25, 50];
  packagesHasRestrictions$: Observable<boolean>;
  showingRestrictedPackages$: Observable<boolean>;
  hidePricing$: Observable<boolean>;

  @ViewChild(MatPaginator) private paginator: MatPaginator;
  constructor(
    private packageActivationTableService: PackageActivationTableService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private vconfigService: VConfigService,
    private accountService: AccountService,
  ) {}

  ngOnInit(): void {
    this.selectedItems$ = this.packageActivationTableService.selectedItems$;
    this.totalPackages$ = this.packageActivationTableService.totalPackages$;

    this.packagesHasRestrictions$ = this.packageActivationTableService.packagesHasRestrictions$;
    this.showingRestrictedPackages$ = this.packageActivationTableService.showingRestrictedPackages$;
    this.hidePricing$ = this.packageActivationTableService.hidePricing$;

    this.activatablePackages$ = this.packageActivationTableService.activatablePackages$.pipe(
      map((activatablePackage) => this.updatePackageWithEditionInfo(activatablePackage)),
    );
  }

  updatePackageWithEditionInfo(activatablePackages: ActivatablePackage[]): ActivatablePackage[] {
    return activatablePackages.map((pkg) => {
      const items: ActivatableItemCount[] = (pkg.activatableItemCount || []).map((item) => {
        if (
          item &&
          item.activatableItem &&
          item.activatableItem.editionData &&
          item.activatableItem.editionData.usesEditions
        ) {
          const edition = item.activatableItem.editionData.editions.find((e) => e.editionId === (item.editionId || ''));
          if (edition) {
            const activeEdition = item.activatableItem.editionData.editions.find(
              (e) => e.editionId === (item.activatableItem.editionData.currentlyActiveEditionId || ''),
            );
            if (activeEdition) {
              return {
                ...item,
                editionName: edition.editionName,
                currentlyActivatedEditionName: activeEdition.editionName,
                activatableItem: { ...item.activatableItem, pricing: edition.pricing },
              };
            }
            return {
              ...item,
              editionName: edition.editionName,
              activatableItem: { ...item.activatableItem, pricing: edition.pricing },
            };
          }
        }
        return item;
      });
      return { ...pkg, activatableItemCount: items };
    });
  }

  extractAppIds(activatablePackages: ActivatablePackage[]): string[] {
    const appIds = (activatablePackages || []).reduce((prev, curr) => {
      const items = (curr.activatableItemCount || []).filter(
        (item) => item.activatableItem.editionData && item.activatableItem.editionData.usesEditions,
      );
      const itemAppIds = items.map((item) => item.activatableItem.appId);
      return prev.concat(itemAppIds);
    }, [] as string[]);

    return appIds.filter((id, idx) => appIds.indexOf(id) === idx);
  }

  onPackageCheckboxClicked(selectedItems: SelectedItem[]): void {
    this.packageActivationTableService.handlePackageCheckboxEvent(selectedItems);
  }

  toggleRestrictedPackages(): void {
    this.packageActivationTableService.toggleShowingRestrictedPackages();
  }

  onPackageSearchTermChanged(searchTerm: string): void {
    this.packageActivationTableService.packageSeachTermChanged(searchTerm);
    // Fix for searching on a page other than the first page
    this.paginator.firstPage();
  }

  onPackagePage(pageEvent: PageEvent): void {
    this.packageActivationTableService.packagePagingChanged(pageEvent);
  }

  onPackageSortOrderChanged(order: SortDirection): void {
    this.packageActivationTableService.packageSortTermChanges(order);
  }
}
